import { Form, Formik } from "formik";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Redirect, useHistory, useParams } from "react-router-dom";
import { Button, Card, CardBody, Col, Row } from "reactstrap";
import * as Yup from "yup";

import { PageLoaderScreen } from "@components/common/PageLoaderScreen";
import { AlertSuccess, AlertError } from "@components/common/AlertToast";
import { CheckSimNao, InputText } from "@components/form";
import { SelectEnum } from "@components/search/enumSelect";
import HorizontalLayout from "@components/layout/HorizontalClean";
import { FIND_CONTA_AGRARE, HAS_AUTHORITY_CORPORATE, LIBERAR_CONTA_AGRARE } from "@handler";
import { FIELD_UNIDADE_AREA, getUnidadeAreaSiglaById } from "@handler/contaAgrare/fieldUnidadeArea";
import "./LiberarConta.css";

const validationSchema = Yup.object().shape({
  nomeConta: Yup.string(),
  codigo: Yup.string(),
  nomeResponsavel: Yup.string(),
  emailResponsavel: Yup.string(),
  whatsapp: Yup.string(),
  moduloAgricultura: Yup.boolean().required("Campo obrigatório"),
  moduloPluviometro: Yup.boolean().required("Campo obrigatório"),
  moduloEstoque: Yup.boolean().required("Campo obrigatório"),
  moduloFinanceiro: Yup.boolean().required("Campo obrigatório"),
  moduloMonitoramentoFiscal: Yup.boolean().required("Campo obrigatório"),
  moduloNFe: Yup.boolean().required("Campo obrigatório"),
  moduloNdvi: Yup.boolean().required("Campo obrigatório"),
  moduloFileStorage: Yup.boolean().required("Campo obrigatório"),
  moduloOFX: Yup.boolean().required("Campo obrigatório"),
  moduloControlePragaDoenca: Yup.boolean().required("Campo obrigatório"),
  trackingEnabled: Yup.boolean().required("Campo obrigatório"),
  numeroUsuario: Yup.string().required("Campo obrigatório"),
  numeroEmitenteFiscal: Yup.string().required("Campo obrigatório"),
  numeroFazenda: Yup.string().required("Campo obrigatório"),
  totalAreaHectare: Yup.string().required("Campo obrigatório"),
  idAreaUnidade: Yup.number().required("Campo obrigatório"),
});

// Conversões em ha (mesmo valor do backend GERAL_UNIDADE_AREA)
const CONVERSAO_HA_BY_ID = {
  1: 1.0,        // Hectares
  2: 2.42,       // Alqueires
  3: 0.404686,   // Acres
};

const LiberarConta = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { id } = useParams();

  const reduxLoading = useSelector(
    (state) => state.entities.contaAgrareReducer?.loading || false
  );
  const registro = useSelector(
    (state) => state.entities.contaAgrareReducer?.registro
  );

  const [contaData, setContaData] = useState({
    nomeConta: "",
    codigo: "",
    nomeResponsavel: "",
    emailResponsavel: "",
    whatsapp: "",
    moduloAgricultura: false,
    moduloPluviometro: false,
    moduloEstoque: false,
    moduloFinanceiro: false,
    moduloMonitoramentoFiscal: false,
    moduloNFe: false,
    moduloNdvi: false,
    moduloFileStorage: false,
    moduloOFX: false,
    moduloControlePragaDoenca: false,
    trackingEnabled: false,
    numeroUsuario: "",
    numeroEmitenteFiscal: "",
    numeroFazenda: "",
    totalAreaHectare: "",
    idAreaUnidade: 1,
  });

  useEffect(() => {
    dispatch({
      type: HAS_AUTHORITY_CORPORATE,
      payload: "PERM_ROLE_WL_REGISTER_FARMER",
    });

    if (registro === undefined || (registro && registro.id !== id)) {
      dispatch({ type: FIND_CONTA_AGRARE, id });
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (registro) {
      setContaData({
        nomeConta: registro.nomeConta || "",
        codigo: registro.accountCode || "",
        nomeResponsavel: registro.nomeResponsavelConta || "",
        emailResponsavel: registro.emailPrincipal || "",
        whatsapp: registro.whatsApp || "",
        moduloAgricultura: registro.licenca?.moduloAgricultura || false,
        moduloPluviometro: registro.licenca?.moduloPluviometro || false,
        moduloEstoque: registro.licenca?.moduloEstoque || false,
        moduloFinanceiro: registro.licenca?.moduloFinanceiro || false,
        moduloMonitoramentoFiscal: registro.licenca?.moduloMonitoramentoFiscal || false,
        moduloNFe: registro.licenca?.moduloNFe || false,
        moduloNdvi: registro.licenca?.moduloNdvi || false,
        moduloFileStorage: registro.licenca?.moduloFileStorage || false,
        moduloOFX: registro.licenca?.moduloOFX || false,
        moduloControlePragaDoenca: registro.licenca?.moduloControlePragaDoenca || false,
        trackingEnabled: registro.trackingEnabled ?? false,
        numeroUsuario: registro.licenca?.numeroUsuario ?? "",
        numeroEmitenteFiscal: registro.licenca?.numeroEmitenteFiscal ?? "",
        numeroFazenda: registro.licenca?.numeroFazenda ?? "",
        totalAreaHectare: registro.licenca?.totalAreaHectare ?? "",
        idAreaUnidade: registro.licenca?.areaUnidade?.id ?? 1,
      });
    }
  }, [registro]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Usa o ID do registro do Redux
      if (!registro?.id) {
        alert("Erro: ID da conta não encontrado.");
        setSubmitting(false);
        return;
      }

      const payload = {
        moduloAgricultura: values.moduloAgricultura,
        moduloPluviometro: values.moduloPluviometro, // Backend espera "moduloPluviometro"
        moduloEstoque: values.moduloEstoque,
        moduloFinanceiro: values.moduloFinanceiro,
        moduloMonitoramentoFiscal: values.moduloMonitoramentoFiscal,
        moduloNdvi: values.moduloNdvi,
        moduloFileStorage: values.moduloFileStorage,
        moduloNFe: values.moduloNFe,
        moduloOFX: values.moduloOFX,
        moduloControlePragaDoenca: values.moduloControlePragaDoenca,
        trackingEnabled: values.trackingEnabled,
        numeroUsuario: parseInt(values.numeroUsuario) || 0,
        numeroEmitenteFiscal: parseInt(values.numeroEmitenteFiscal) || 0,
        numeroFazenda: parseInt(values.numeroFazenda) || 0,
        totalAreaHectare: parseFloat(values.totalAreaHectare) || 0,
        idAreaUnidade: parseInt(values.idAreaUnidade) || 1,
      };

      await new Promise((resolve, reject) => {
        dispatch({
          type: LIBERAR_CONTA_AGRARE,
          id: registro.id,
          data: payload,
          resolve,
          reject,
        });
      });

      AlertSuccess("Conta liberada com sucesso!");
      history.push("/corporate/contaProdutor");
    } catch (error) {
      console.error("Erro ao liberar conta:", error);
      setSubmitting(false);
      AlertError("Erro ao liberar conta. Por favor, tente novamente.");
    }
  };

  // Mostra loading enquanto carrega os dados
  if (reduxLoading || registro === undefined) {
    return <PageLoaderScreen />;
  }

  // Se terminou de carregar mas não encontrou o registro, redireciona
  if (!reduxLoading && registro === null) {
    return <Redirect to="/corporate/contaProdutor" />;
  }

  return (
    <HorizontalLayout>
      <PageLoaderScreen isOpen={reduxLoading} />

      <div className="page-content">
        <div className="container-fluid">
          <div className="page-title-box">
            <h4 className="mb-0">Liberação Conta Agrare</h4>
          </div>

          <Card>
            <CardBody>
              <Formik
                initialValues={contaData}
                validationSchema={validationSchema}
                onSubmit={handleSubmit} 
                enableReinitialize={true}
              >
                {({ isSubmitting, values }) => (
                    <Form>
                      <h5 className="mb-3">Dados da Conta Selecionada</h5>
                      <Row>
                        <InputText
                          col={6}
                          name="nomeConta"
                          label="Nome da conta:"
                          disabled
                        />
                        <InputText
                          col={6}
                          name="codigo"
                          label="Código:"
                          disabled
                        />
                      </Row>

                      <Row>
                        <InputText
                          col={4}
                          name="nomeResponsavel"
                          label="Nome Responsável:"
                          disabled
                        />
                        <InputText
                          col={4}
                          name="emailResponsavel"
                          label="E-mail Responsável:"
                          disabled
                        />
                        <InputText
                          col={4}
                          name="whatsapp"
                          label="WhatsApp"
                          disabled
                        />
                      </Row>

                      <hr className="my-4" />

                      <h5 className="mb-3">Módulos e Licenças</h5>
                      <Row>
                        <CheckSimNao
                          col={3}
                          name="moduloAgricultura"
                          label="Módulo Agricultura"
                        />
                        <CheckSimNao
                          col={3}
                          name="moduloPluviometro"
                          label="Módulo Pluviômetro"
                        />
                        <CheckSimNao
                          col={3}
                          name="moduloEstoque"
                          label="Módulo Estoque"
                        />
                        <CheckSimNao
                          col={3}
                          name="moduloFinanceiro"
                          label="Módulo Financeiro"
                        />
                      </Row>

                      <Row>
                        <CheckSimNao
                          col={3}
                          name="moduloMonitoramentoFiscal"
                          label="Módulo Monitoramento Fiscal"
                        />
                        <CheckSimNao
                          col={3}
                          name="moduloNdvi"
                          label="Módulo NDVI"
                        />
                        <CheckSimNao
                          col={3}
                          name="moduloFileStorage"
                          label="Módulo Armazenamento de Arquivos"
                        />
                        <CheckSimNao
                          col={3}
                          name="moduloNFe"
                          label="Módulo Emissão NFe"
                        />
                      </Row>

                      <Row>
                        <CheckSimNao
                          col={3}
                          name="moduloOFX"
                          label="Módulo OFX"
                        />
                        <CheckSimNao
                          col={3}
                          name="moduloControlePragaDoenca"
                          label="Módulo Controle Praga/Doença"
                        />
                        <CheckSimNao
                          col={3}
                          name="trackingEnabled"
                          label="Tracking"
                        />
                      </Row>

                      <hr className="my-4" />

                      <h5 className="mb-3">Limites da Licença</h5>
                      <Row>
                        <Col md={3}>
                          <InputText
                            col={12}
                            name="numeroUsuario"
                            label="Número de Usuários"
                          />
                          <small className="form-text text-muted">
                            0 = ilimitado
                          </small>
                        </Col>
                        <Col md={3}>
                          <InputText
                            col={12}
                            name="numeroEmitenteFiscal"
                            label="Número de Emitente Fiscal"
                          />
                          <small className="form-text text-muted">
                            0 = ilimitado
                          </small>
                        </Col>
                        <Col md={3}>
                          <InputText
                            col={12}
                            name="numeroFazenda"
                            label="Número de Fazendas"
                          />
                          <small className="form-text text-muted">
                            0 = ilimitado
                          </small>
                        </Col>
                      </Row>
                      <Row className="mt-3">
                        <Col md={3}>
                          <InputText
                            col={12}
                            name="totalAreaHectare"
                            label="Limite de Área (ha)"
                          />
                          <small className="form-text text-muted">
                            0 = ilimitado
                          </small>
                        </Col>
                        <SelectEnum
                          name="idAreaUnidade"
                          label="Unidade de Área do Cliente"
                          placeholder="Selecione a Unidade"
                          list={FIELD_UNIDADE_AREA}
                          col={3}
                        />
                        <Col md={3}>
                          <label className="form-label">
                            Área na unidade do cliente ({getUnidadeAreaSiglaById(parseInt(values.idAreaUnidade)) || "ha"})
                          </label>
                          <input
                            className="form-control"
                            disabled
                            value={(() => {
                              const ha = parseFloat(values.totalAreaHectare) || 0;
                              if (ha === 0) return "Ilimitado";
                              const conversao = CONVERSAO_HA_BY_ID[parseInt(values.idAreaUnidade)] || 1;
                              return (ha / conversao).toFixed(2);
                            })()}
                          />
                        </Col>
                      </Row>

                      <Row className="mt-4">
                        <Col>
                          <Button className="btn-primary" onClick={() => { }} type='submit'>
                            <i className='fa fa-check' /> Salvar
                          </Button>

                          <Link to='/corporate/contaProdutor' title='Voltar' style={{ marginLeft: '10px' }}>
                            Voltar
                          </Link>
                        </Col>
                      </Row>
                    </Form>
                )}
              </Formik>
            </CardBody>
          </Card>
        </div>
      </div>
    </HorizontalLayout>
  );
};

export default LiberarConta;
